import React from 'react';
import { View, Pressable, Image, StyleSheet, ActivityIndicator, Platform } from 'react-native';
import { useTranslation } from '@/hooks/useTranslation';
import Icon from '@/components/Icon';
import { HistoryText as Text, useHistoryColors, HistoryMenu } from './ToolHistoryUI';
import { OUTPUT_TYPE_ICON, getFileUrl, getRunStatusVisual, toolRunResultPreviewSnippet } from './toolDetailHelpers';
import { cleanHistoryText, getHistoryTitle, getHistoryToolName, getHistoryDate } from './toolHistoryPresentation';

function ToolHistoryCard({ run, isDeleting = false, onOpen, onDelete, compact = false }) {
  const colors = useHistoryColors();
  const { t, currentLanguage } = useTranslation();
  const file = run?.resultFiles?.[0];
  const mime = file?.mimeType || '';
  const image = mime.startsWith('image/') ? getFileUrl(file)
    : file?.thumbnailPath || file?.thumbnailUrl || file?.previewUrl;
  const video = mime.startsWith('video/') && !image ? getFileUrl(file) : null;
  const isAudio = /^(audio|music)/.test(mime || run?.tool?.outputType || '');
  const icon = isAudio ? 'waveform-lines' : OUTPUT_TYPE_ICON[run?.tool?.outputType] || 'file-lines';
  const title = getHistoryTitle(run);
  const toolName = getHistoryToolName(run);
  const date = getHistoryDate(run?.createdAt, currentLanguage);
  const preview = cleanHistoryText(toolRunResultPreviewSnippet(run, 300));
  const status = getRunStatusVisual(run?.status, colors, {}, t);
  const openLabel = `${t('pages.tools.openInTool')}: ${title}${date ? ` · ${date}` : ''}`;
  const showStatus = run?.status && run.status !== 'completed';

  return <View style={[styles.card, compact && styles.row, { backgroundColor: colors.solid_color_block,
    borderColor: colors.border_color_secondary, opacity: isDeleting ? 0.6 : 1 }]}>
    <Pressable onPress={() => onOpen?.(run)} accessibilityRole="button" accessibilityLabel={openLabel}
      style={({ hovered, pressed }) => [styles.open, compact && styles.rowOpen,
        (hovered || pressed) && { backgroundColor: colors.solid_color_secondary }]}>
      <View style={[compact ? styles.smallPreview : styles.preview, { backgroundColor: colors.solid_color_secondary }]}>
        {image ? <Image source={{ uri: image }} style={styles.image} resizeMode="cover" accessibilityIgnoresInvertColors />
          : video && !compact && Platform.OS === 'web'
            // eslint-disable-next-line react/forbid-elements
            ? <video src={video} muted playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} />
          : compact || isAudio || !preview ? <Icon name={icon} size={compact ? 20 : 36} color={colors.text_color_secondary} />
            : <View style={styles.documentPreview}>
                <Icon name={icon} size={20} color={colors.text_color_secondary} />
                <Text size="s" color="secondary" numberOfLines={4} style={{ lineHeight: 22 }}>{preview}</Text>
              </View>}
      </View>
      <View style={[styles.body, compact && styles.rowBody]}>
        <Text weight="semibold" size="s" numberOfLines={2} style={{ lineHeight: 21, minHeight: compact ? undefined : 42 }}>{title}</Text>
        <View style={styles.meta}>
          <Text size="xs" color="secondary" numberOfLines={1} style={{ flexShrink: 1 }}>{toolName}</Text>
          {!!date && <Text size="xs" color="secondary">· {date}</Text>}
        </View>
        {showStatus && <View style={styles.status}>
          <Icon name={status.icon} size={11} color={status.color} />
          <Text size="xs" style={{ color: status.color }}>{status.label}</Text>
        </View>}
      </View>
    </Pressable>
    {!!onDelete && <View style={[styles.menu, compact && styles.rowMenu]}>
      {isDeleting ? <View style={styles.deleting}><ActivityIndicator size="small" color={colors.text_color_secondary} /></View>
        : <HistoryMenu label="" accessibilityLabel={`${t('pages.tools.historyRunActions')}: ${title}`}
            icon="ellipsis" options={[{ id: 'open', label: t('pages.tools.openInTool'), icon: 'arrow-up-right-from-square' },
              { id: 'delete', label: t('pages.tools.deleteRun'), icon: 'trash' }]}
            onChange={action => action === 'delete' ? onDelete(run) : onOpen?.(run)} />}
    </View>}
  </View>;
}
const styles = StyleSheet.create({
  card: { width: '100%', borderWidth: 1, borderRadius: 16, overflow: 'hidden' },
  open: { width: '100%' },
  preview: { width: '100%', height: 172, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  image: { width: '100%', height: '100%' },
  documentPreview: { width: '100%', padding: 22, gap: 14 },
  body: { padding: 16, paddingRight: 54, gap: 7 },
  meta: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 4 },
  status: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 3 },
  menu: { position: 'absolute', bottom: 9, right: 4 },
  deleting: { width: 44, height: 44, alignItems: 'center', justifyContent: 'center' },
  row: { borderRadius: 12, flexDirection: 'row', alignItems: 'center' },
  rowOpen: { flex: 1, flexDirection: 'row', alignItems: 'center', padding: 12, paddingRight: 52, gap: 14 },
  smallPreview: { width: 56, height: 56, borderRadius: 8, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  rowBody: { flex: 1, minWidth: 0, padding: 0, paddingRight: 0, gap: 4 },
  rowMenu: { top: '50%', marginTop: -22, bottom: undefined },
});
export default React.memo(ToolHistoryCard);
